import React from "react";
import { useDrag, useDrop, DragPreviewImage } from "react-dnd";

import Color from "../Color";
import ItemTypes from "../ItemTypes";
import arrayIdFinder from "../helpers/arrayIdFinder";

import Text from "./Text";
import EmployeeIcons from "./EmployeeIcons";

const WIDTH = 400;
const HEIGHT = 200;

function Card({
  id,
  x,
  y,
  entityName,
  parentId,
  employees,
  employeeDetails,
  lineManager,
  onContextMenu,
  toggleCollapse,
  isCollapsed,
  addEmployees,
  isRoot,
  openLineManager,
  onAdd,
  onEdit,
  onDelete,
  onEntityDrop,
  isAncestor,
  startEntityDragging,
  stopEntityDragging
}) {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [ItemTypes.EMPLOYEE, ItemTypes.ENTITY],
    drop: ({ type, entityId, selected, idDragged }) => {
      if (type === ItemTypes.ENTITY) {
        onEntityDrop(id, entityId);
        return;
      }
      const selected_arr = Object.keys(selected).filter(
        k => selected[k] && k !== idDragged
      );
      addEmployees(id, [...selected_arr]);
    },
    canDrop: ({ type, entityId, entityParentId }) => {
      if (
        type === ItemTypes.ENTITY &&
        (entityId === id || entityParentId === id || isAncestor(id, entityId))
      )
        return false;
      return true;
    },
    collect: mon => ({
      isOver: !!mon.isOver(),
      canDrop: !!mon.canDrop()
    })
  });

  const [{ isDragging }, drag, preview] = useDrag({
    item: { type: ItemTypes.ENTITY, entityId: id, entityParentId: parentId },
    collect: mon => ({
      isDragging: !!mon.isDragging()
    }),
    begin: startEntityDragging,
    end: stopEntityDragging
  });
  const lineManagerDetails = arrayIdFinder(employeeDetails, lineManager);

  const noEmployees = !employees || !employees.length;
  return (
    <g
      ref={drop}
      transform={`translate(${x},${y})`}
      filter={
        isOver && canDrop
          ? "url('#colorMeHueRotate')"
          : isDragging
          ? "url('#colorMeSaturate')"
          : undefined
      }
    >
      <DragPreviewImage connect={preview} src={"/dragPreview.png"} />

      <rect rx={10} width={WIDTH} height={HEIGHT} fill={"url(#gradient)"} />

      <Text
        width={WIDTH * 0.9}
        x={WIDTH * 0.5}
        y={HEIGHT * 0.25}
        textAnchor="middle"
        fontSize={30}
        fontWeight="400"
        fill={Color.initial}
        text={entityName}
      />

      <foreignObject x="16" y="16" width="24" height="24">
        <img
          ref={drag}
          alt="Drag"
          className="clickable"
          width="24"
          height="24"
          src="/orgchart/icons/move.svg"
        />
      </foreignObject>

      <image
        onClick={e => onEdit(id)}
        className="clickable"
        x={WIDTH - 40}
        y="16"
        width="24"
        height="24"
        href="/orgchart/icons/edit.svg"
      />

      <g onClick={toggleCollapse} className="clickable">
        <circle
          r={18}
          cx={WIDTH * 0.5}
          cy={HEIGHT - 6}
          fill={Color.gradientMid}
          stroke={Color.initial}
          strokeWidth={1}
        />
        <image
          x={WIDTH * 0.5 - 10}
          y={HEIGHT - 6 - 10}
          width="20"
          height="20"
          href={`/orgchart/icons/${isCollapsed ? "up" : "down"}-chevron.svg`}
        />
      </g>

      {noEmployees && (
        <g>
          <Text
            x={WIDTH * 0.05}
            y={HEIGHT * 0.5}
            fontSize={20}
            fill={Color.initial}
            width={WIDTH * 0.95}
            text={
              isRoot
                ? "Drag organization chairman/founder/CEO"
                : "Drag employee(s) here"
            }
          />
        </g>
      )}

      {!noEmployees && lineManager && (
        <g>
          <Text
            x={WIDTH * 0.5}
            fill={Color.initial}
            textAnchor="middle"
            y={HEIGHT * 0.4}
            width={WIDTH * 0.9}
            fontSize={20}
            text={lineManagerDetails.name}
          />
          <Text
            x={WIDTH * 0.5}
            fill={Color.initial}
            textAnchor="middle"
            y={HEIGHT * 0.4 + 30}
            width={WIDTH * 0.9}
            fontSize={20}
            text={lineManagerDetails.position}
          />
        </g>
      )}

      {!noEmployees && (
        <Text
          className="clickable"
          textDecoration="underline"
          fill={Color.initial}
          onClick={() => openLineManager(id)}
          x={WIDTH * 0.05}
          y={HEIGHT * 0.7}
          width={WIDTH * 0.9}
          fontSize={14}
          text="Select a line manager"
        />
      )}

      <g
        onClick={() => openLineManager(id)}
        className="clickable"
        transform={`translate(${WIDTH * 0.15},${HEIGHT - 12})`}
      >
        <EmployeeIcons
          employeeDetails={employeeDetails}
          employees={employees}
          lineManager={lineManagerDetails}
        />
      </g>

      <g transform={`translate(${WIDTH},${HEIGHT - 40})`}>
        <image
          onClick={e => onDelete(id)}
          className="clickable"
          x="-72"
          y="0"
          width="24"
          height="24"
          href="/orgchart/icons/delete.svg"
        />
        <image
          onClick={e => onAdd(id)}
          className="clickable"
          x="-40"
          y="0"
          width="24"
          height="24"
          href="/orgchart/icons/plus.svg"
        />
      </g>
    </g>
  );
}

export default Card;
